<script setup lang="ts">
type Props = {
  src?: string | null
  alt?: string | null
  ratio?: '1:1' | '2:3' | '3:2' | '16:9' | '16:10'
  class?: string | object
}

const props = defineProps<Props>()

const hasError = ref(false)

const classes = computed(() => ({
  'app-image': true,
  [`app-image--ratio-${props.ratio}`]: !!props.ratio,
  'app-image--ratio-default': !props.ratio && !props.src,
  [`${props.class}`]: !!props.class,
}))
</script>

<template>
  <figure :class="classes">
    <div class="ratio"></div>

    <img
      v-if="src && !hasError"
      :src="src"
      :alt="alt ?? ''"
      loading="lazy"
      @error="hasError = true"
    />

    <IllustrationBalloon v-if="!src || hasError" />

    <slot></slot>
  </figure>
</template>

<style lang="scss">
@use 'sass:math';

@import '@boats/ui/styles/config';

.app-image {
  position: relative;
  color: $gray-300;
  background-color: $gray-100;
  border-radius: 0.5rem;
  overflow: hidden;

  img {
    width: 100%;
    height: auto;

    &[data-error] {
      display: none;
    }
  }

  &[class*='app-image--ratio-'] img {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    object-fit: cover;
  }

  .illustration-balloon {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 50%;
    max-width: 5rem;
    height: 50%;
    max-height: 5rem;
    transform: translate(-50%, -50%);
  }
}

.app-image--ratio-1\:1 {
  .ratio {
    padding-top: percentage(math.div(1, 1));
  }
}

.app-image--ratio-2\:3 {
  .ratio {
    padding-top: percentage(math.div(3, 2));
  }
}

.app-image--ratio-3\:2 {
  .ratio {
    padding-top: percentage(math.div(2, 3));
  }
}

.app-image--ratio-16\:9 {
  .ratio {
    padding-top: percentage(math.div(9, 16));
  }
}

.app-image--ratio-16\:10,
.app-image--ratio-default {
  .ratio {
    padding-top: percentage(math.div(10, 16));
  }
}
</style>
